import { MAP_FURNITURE_KINDS, MAP_REGISTRY } from "@/lib/office/personal-office-layout";
import { resolveFurnitureSprite } from "@/lib/office/sprites";
import { cn } from "@/lib/utils";
import { CAMPUS_BUILDING_PRESETS, type PaletteItem } from "./map-layout-utils";

const ROOM_ITEMS: Array<{ item: PaletteItem; label: string }> = [
  { item: { type: "office" }, label: "Office" },
  { item: { type: "zone", zoneTemplate: "meeting" }, label: "Meeting" },
  { item: { type: "zone", zoneTemplate: "huddle" }, label: "Huddle" },
  { item: { type: "zone", zoneTemplate: "lounge" }, label: "Lounge" },
  { item: { type: "zone", zoneTemplate: "focus" }, label: "Focus" },
  { item: { type: "zone", zoneTemplate: "path" }, label: "Cobble path" },
  { item: { type: "zone", zoneTemplate: "cobble_round" }, label: "Round cobbles" },
  { item: { type: "zone", zoneTemplate: "cobble_sand" }, label: "Sandstone cobbles" },
  { item: { type: "zone", zoneTemplate: "brick" }, label: "Red brick" },
  { item: { type: "zone", zoneTemplate: "brick_dark" }, label: "Dark brick" },
  { item: { type: "zone", zoneTemplate: "sidewalk" }, label: "Sidewalk" },
  { item: { type: "zone", zoneTemplate: "pavement" }, label: "Pavement" },
  { item: { type: "zone", zoneTemplate: "dirt" }, label: "Dirt path" },
  { item: { type: "zone", zoneTemplate: "deck" }, label: "Wood deck" },
  { item: { type: "zone", zoneTemplate: "park" }, label: "Park grass" },
  { item: { type: "spawn" }, label: "Spawn" },
];

/** Outdoor props commonly used on campus (short list, full list below). */
const OUTDOOR_KINDS = [
  "building",
  "door",
  "tree",
  "pine",
  "bush",
  "hedge",
  "flower",
  "flowerbed",
  "planter",
  "rock",
  "bench",
  "streetlamp",
  "bollard",
  "fence",
  "fountain",
  "plaza_sign",
  "cafe_umbrella",
  "delivery_truck",
] as const;

type Props = {
  palette: PaletteItem | null;
  onSelect: (item: PaletteItem | null) => void;
  /** When true, emphasize campus outdoor tools. */
  outdoor?: boolean;
};

function samePalette(a: PaletteItem | null, b: PaletteItem): boolean {
  if (!a) return false;
  if (a.type !== b.type) return false;
  if (a.type === "zone" && b.type === "zone") return a.zoneTemplate === b.zoneTemplate;
  if (a.type === "furniture" && b.type === "furniture") return a.furnitureKind === b.furnitureKind;
  if (a.type === "building" && b.type === "building") return a.style === b.style;
  if (a.type === "transition" && b.type === "transition") return a.toMapId === b.toMapId;
  return true;
}

function FurnitureThumb({ kind }: { kind: string }) {
  const resolved = resolveFurnitureSprite(kind, "S");
  const src = resolved.thumbSrc ?? resolved.asset?.src ?? null;
  if (!src) {
    return (
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded bg-muted text-[10px] font-semibold uppercase text-muted-foreground">
        {kind.slice(0, 2)}
      </span>
    );
  }
  return (
    <img
      src={src}
      alt=""
      width={32}
      height={32}
      className="h-8 w-8 shrink-0 rounded border border-border bg-muted object-contain"
      loading="lazy"
      draggable={false}
    />
  );
}

function PaletteButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex w-full items-center gap-2 rounded-md border px-2 py-1.5 text-left text-xs transition-colors",
        active ? "border-primary bg-primary/10 text-foreground" : "border-border hover:bg-muted",
      )}
    >
      {children}
    </button>
  );
}

export function MapLayoutPalette({ palette, onSelect, outdoor = false }: Props) {
  const indoorMaps = MAP_REGISTRY.filter((m) => m.theme === "indoor");

  return (
    <div className="flex h-full flex-col gap-3 overflow-y-auto p-3">
      {outdoor ? (
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Campus buildings
          </p>
          <p className="mb-2 text-[10px] leading-snug text-muted-foreground">
            Click a building, then click the map. Places multi-tile shell + door + enter transition.
            Drag to move; edit size/door in the inspector.
          </p>
          <div className="flex flex-col gap-1">
            {CAMPUS_BUILDING_PRESETS.map((p) => {
              const item: PaletteItem = {
                type: "building",
                style: p.style,
                label: p.label,
                color: p.color,
                w: p.w,
                d: p.d,
                toMapId: p.toMapId,
              };
              return (
                <PaletteButton
                  key={p.style}
                  active={samePalette(palette, item)}
                  onClick={() => onSelect(samePalette(palette, item) ? null : item)}
                >
                  <span
                    className="h-8 w-8 shrink-0 rounded border border-border"
                    style={{ background: p.color }}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium">{p.label}</span>
                    <span className="text-[10px] text-muted-foreground">
                      {p.w}×{p.d} → {p.toMapId}
                    </span>
                  </span>
                </PaletteButton>
              );
            })}
          </div>
        </div>
      ) : null}

      <div>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Rooms / zones
        </p>
        <div className="flex flex-col gap-1">
          {ROOM_ITEMS.map(({ item, label }) => (
            <PaletteButton
              key={label}
              active={samePalette(palette, item)}
              onClick={() => onSelect(samePalette(palette, item) ? null : item)}
            >
              <span className="truncate">{label}</span>
            </PaletteButton>
          ))}
        </div>
      </div>

      {outdoor ? (
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Enter transition
          </p>
          <div className="flex flex-col gap-1">
            {indoorMaps.map((m) => {
              const item: PaletteItem = {
                type: "transition",
                toMapId: m.id,
                label: m.label,
              };
              return (
                <PaletteButton
                  key={m.id}
                  active={samePalette(palette, item)}
                  onClick={() => onSelect(samePalette(palette, item) ? null : item)}
                >
                  <span className="truncate">→ {m.label}</span>
                </PaletteButton>
              );
            })}
          </div>
        </div>
      ) : null}

      <div>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {outdoor ? "Outdoor props" : "Furniture"}
        </p>
        <div className="grid grid-cols-1 gap-1">
          {(outdoor ? OUTDOOR_KINDS : MAP_FURNITURE_KINDS).map((kind) => {
            const item: PaletteItem = { type: "furniture", furnitureKind: kind };
            return (
              <PaletteButton
                key={kind}
                active={samePalette(palette, item)}
                onClick={() => onSelect(samePalette(palette, item) ? null : item)}
              >
                <FurnitureThumb kind={kind} />
                <span className="truncate capitalize">{kind.replaceAll("_", " ")}</span>
              </PaletteButton>
            );
          })}
        </div>
        {outdoor ? (
          <details className="mt-2">
            <summary className="cursor-pointer text-[10px] text-muted-foreground">
              All furniture kinds
            </summary>
            <div className="mt-1 grid grid-cols-1 gap-1">
              {MAP_FURNITURE_KINDS.filter(
                (k) => !(OUTDOOR_KINDS as readonly string[]).includes(k),
              ).map((kind) => {
                const item: PaletteItem = { type: "furniture", furnitureKind: kind };
                return (
                  <PaletteButton
                    key={kind}
                    active={samePalette(palette, item)}
                    onClick={() => onSelect(samePalette(palette, item) ? null : item)}
                  >
                    <FurnitureThumb kind={kind} />
                    <span className="truncate capitalize">{kind}</span>
                  </PaletteButton>
                );
              })}
            </div>
          </details>
        ) : null}
      </div>

      <p className="text-xs text-muted-foreground">
        Select a tool, then click the map to place (ghost shows footprint). Multi-select with
        Shift/⌘-click or marquee · drag to move · SE handle resizes · Delete removes.{" "}
        <strong>Publish</strong> pushes live.
      </p>
    </div>
  );
}
